import { useState, useEffect } from 'react';
import { getSubscriptionLifecycleEvents } from '../../services/analyticsSubscriptionEventsService';
import { getAnalyticsEvents } from '../../services/analyticsService';
import BarChart from '../charts/BarChart';
import LineChart from '../charts/LineChart';

const FunnelsTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [funnelData, setFunnelData] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [subEvents, analyticsEvents] = await Promise.all([
          getSubscriptionLifecycleEvents(30),
          getAnalyticsEvents(30),
        ]);

        const paywallShown =
          (subEvents.byEventType['paywall_shown'] || 0) +
          analyticsEvents.filter((e) => e.eventName === 'paywall_shown').length;

        const paywallCTA =
          (subEvents.byEventType['paywall_cta_clicked'] || 0) +
          analyticsEvents.filter((e) => e.eventName === 'paywall_cta_clicked').length;

        const subscriptionStarted =
          (subEvents.byEventType['subscription_started'] || 0) +
          analyticsEvents.filter((e) => e.eventName === 'subscription_started').length;

        const subscriptionCompleted =
          (subEvents.byEventType['subscription_completed'] || 0) +
          (subEvents.byEventType['purchase_verified'] || 0) +
          analyticsEvents.filter((e) => e.eventName === 'subscription_completed').length;

        const paywallDismissed =
          (subEvents.byEventType['paywall_dismissed'] || 0) +
          analyticsEvents.filter((e) => e.eventName === 'paywall_dismissed').length;

        // Calculate conversion rates
        const ctaConversionRate = paywallShown > 0 ? (paywallCTA / paywallShown) * 100 : 0;
        const startConversionRate = paywallCTA > 0 ? (subscriptionStarted / paywallCTA) * 100 : 0;
        const completeConversionRate = subscriptionStarted > 0 ? (subscriptionCompleted / subscriptionStarted) * 100 : 0;
        const overallConversionRate = paywallShown > 0 ? (subscriptionCompleted / paywallShown) * 100 : 0;

        // Calculate drop-offs
        const dropOffAtCTA = paywallShown - paywallCTA;
        const dropOffAtStart = paywallCTA - subscriptionStarted;
        const dropOffAtComplete = subscriptionStarted - subscriptionCompleted;

        setFunnelData({
          paywallShown,
          paywallCTA,
          subscriptionStarted,
          subscriptionCompleted,
          paywallDismissed,
          ctaConversionRate,
          startConversionRate,
          completeConversionRate,
          overallConversionRate,
          dropOffAtCTA,
          dropOffAtStart,
          dropOffAtComplete,
        });
      } catch (error) {
        console.error('Error loading funnel data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading funnel data...</p>
      </div>
    );
  }

  if (!funnelData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No funnel data available</p>
      </div>
    );
  }

  const funnelStepsData = {
    labels: ['Paywall Shown', 'CTA Clicked', 'Started', 'Completed'],
    datasets: [
      {
        label: 'Users',
        data: [
          funnelData.paywallShown,
          funnelData.paywallCTA,
          funnelData.subscriptionStarted,
          funnelData.subscriptionCompleted,
        ],
        backgroundColor: ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6'],
      },
    ],
  };

  const conversionRatesData = {
    labels: ['CTA Click', 'Start', 'Complete', 'Overall'],
    datasets: [
      {
        label: 'Conversion Rate (%)',
        data: [
          funnelData.ctaConversionRate,
          funnelData.startConversionRate,
          funnelData.completeConversionRate,
          funnelData.overallConversionRate,
        ],
        borderColor: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.1)',
        fill: true,
      },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Conversion Funnels
      </h1>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Overall Conversion</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {funnelData.overallConversionRate.toFixed(2)}%
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Paywall Shown</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {funnelData.paywallShown}
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Completed</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {funnelData.subscriptionCompleted}
          </h2>
        </div>
      </div>

      {/* Funnel Visualization */}
      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
      }}>
        <h3 style={{ marginTop: 0 }}>Conversion Funnel</h3>
        <BarChart data={funnelStepsData} height={300} />
      </div>

      {/* Conversion Rates */}
      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
      }}>
        <h3 style={{ marginTop: 0 }}>Conversion Rates by Stage</h3>
        <LineChart data={conversionRatesData} height={300} />
      </div>

      {/* Funnel Details Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}>
        <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
          Funnel Breakdown
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Stage</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Count</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Drop-off</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Conversion Rate</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '1rem' }}>Paywall Shown</td>
              <td style={{ padding: '1rem' }}>{funnelData.paywallShown}</td>
              <td style={{ padding: '1rem', color: '#ef4444' }}>
                {funnelData.paywallDismissed} dismissed
              </td>
              <td style={{ padding: '1rem' }}>-</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '1rem' }}>CTA Clicked</td>
              <td style={{ padding: '1rem' }}>{funnelData.paywallCTA}</td>
              <td style={{ padding: '1rem', color: '#ef4444' }}>
                {funnelData.dropOffAtCTA} dropped
              </td>
              <td style={{ padding: '1rem', color: '#10b981' }}>
                {funnelData.ctaConversionRate.toFixed(2)}%
              </td>
            </tr>
            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '1rem' }}>Subscription Started</td>
              <td style={{ padding: '1rem' }}>{funnelData.subscriptionStarted}</td>
              <td style={{ padding: '1rem', color: '#ef4444' }}>
                {funnelData.dropOffAtStart} dropped
              </td>
              <td style={{ padding: '1rem', color: '#10b981' }}>
                {funnelData.startConversionRate.toFixed(2)}%
              </td>
            </tr>
            <tr>
              <td style={{ padding: '1rem' }}>Subscription Completed</td>
              <td style={{ padding: '1rem', fontWeight: 'bold' }}>
                {funnelData.subscriptionCompleted}
              </td>
              <td style={{ padding: '1rem', color: '#ef4444' }}>
                {funnelData.dropOffAtComplete} dropped
              </td>
              <td style={{ padding: '1rem', color: '#10b981', fontWeight: 'bold' }}>
                {funnelData.completeConversionRate.toFixed(2)}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FunnelsTab;

